import React, { useState, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FiX, FiCopy, FiLink, FiCheck, FiTrash2, FiBell, FiLock, FiClock } from 'react-icons/fi';
import { DeckWithStats, DeckShareLink, getShareUrl, formatDate } from '../../types/deck';
import { deckService } from '../../services/deckService';

// Type cast icons for React 18 compatibility
const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;
const CopyIcon = FiCopy as React.FC<React.SVGProps<SVGSVGElement>>;
const LinkIcon = FiLink as React.FC<React.SVGProps<SVGSVGElement>>;
const CheckIcon = FiCheck as React.FC<React.SVGProps<SVGSVGElement>>;
const TrashIcon = FiTrash2 as React.FC<React.SVGProps<SVGSVGElement>>;
const BellIcon = FiBell as React.FC<React.SVGProps<SVGSVGElement>>;
const LockIcon = FiLock as React.FC<React.SVGProps<SVGSVGElement>>;
const ClockIcon = FiClock as React.FC<React.SVGProps<SVGSVGElement>>;

interface ShareLinkModalProps {
  deck: DeckWithStats;
  onClose: () => void;
}

const ShareLinkModal: React.FC<ShareLinkModalProps> = ({
  deck,
  onClose,
}) => {
  const [shareLinks, setShareLinks] = useState<DeckShareLink[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadShareLinks = useCallback(async () => {
    try {
      setIsLoading(true);
      const links = await deckService.getShareLinks(deck.id);
      setShareLinks(links);
    } catch (err: any) {
      setError(err?.message || 'Failed to load share links');
    } finally {
      setIsLoading(false);
    }
  }, [deck.id]);

  useEffect(() => {
    loadShareLinks();
  }, [loadShareLinks]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isCreating) {
      onClose();
    }
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isCreating) {
        onClose();
      }
    },
    [onClose, isCreating]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const handleCreateLink = async () => {
    setIsCreating(true);
    setError(null);
    try {
      const newLink = await deckService.createShareLink(deck.id);
      setShareLinks((prev) => [newLink, ...prev]);
    } catch (err: any) {
      setError(err?.message || 'Failed to create share link');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopyLink = async (urlSlug: string | null, shareToken: string, linkId: string) => {
    const url = getShareUrl(urlSlug || shareToken, deck.name);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(linkId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setError('Failed to copy link to clipboard');
    }
  };

  const handleToggleNotifications = async (link: DeckShareLink) => {
    try {
      const updated = await deckService.updateShareLink(deck.id, link.id, {
        email_notifications: !link.email_notifications,
      });
      setShareLinks((prev) =>
        prev.map((l) => (l.id === link.id ? updated : l))
      );
    } catch (err: any) {
      setError(err?.message || 'Failed to update notifications');
    }
  };

  const handleToggleActive = async (link: DeckShareLink) => {
    try {
      const updated = await deckService.updateShareLink(deck.id, link.id, {
        is_active: !link.is_active,
      });
      setShareLinks((prev) =>
        prev.map((l) => (l.id === link.id ? updated : l))
      );
    } catch (err: any) {
      setError(err?.message || 'Failed to update link');
    }
  };

  const handleDelete = async (linkId: string) => {
    try {
      await deckService.deleteShareLink(deck.id, linkId);
      setShareLinks((prev) => prev.filter((l) => l.id !== linkId));
    } catch (err: any) {
      setError(err?.message || 'Failed to delete link');
    }
  };

  const modal = (
    <div className="su-deck-modal-overlay" onClick={handleOverlayClick}>
      <div className="su-deck-modal" style={{ maxWidth: '600px' }} role="dialog" aria-modal="true" aria-labelledby="share-modal-title">
        <div className="su-deck-modal-header">
          <div>
            <h2 id="share-modal-title" className="su-deck-modal-title">Share Deck</h2>
            <p className="su-deck-modal-subtitle">
              Create and manage share links for {deck.name}
            </p>
          </div>
          <button className="su-deck-modal-close" onClick={onClose} aria-label="Close modal">
            <CloseIcon width={20} height={20} />
          </button>
        </div>

        <div className="su-deck-modal-body">
          {error && (
            <div className="su-deck-error-message">
              {error}
            </div>
          )}

          {/* Create new link button */}
          <button
            className="su-btn-primary"
            onClick={handleCreateLink}
            disabled={isCreating}
            style={{ width: '100%', marginBottom: '20px' }}
          >
            <LinkIcon width={16} height={16} />
            <span>{isCreating ? 'Creating...' : 'Create New Share Link'}</span>
          </button>

          {/* Loading state */}
          {isLoading && (
            <div style={{ textAlign: 'center', padding: '32px 16px', color: '#666' }}>
              Loading share links...
            </div>
          )}

          {/* Share links list */}
          {!isLoading && shareLinks.length === 0 && (
            <div style={{ textAlign: 'center', padding: '32px 16px', color: '#666' }}>
              <LinkIcon style={{ width: 32, height: 32, marginBottom: 12, opacity: 0.5 }} />
              <p style={{ margin: 0 }}>No share links yet</p>
              <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#888' }}>
                Create a link to share this deck with investors
              </p>
            </div>
          )}

          {!isLoading && shareLinks.length > 0 && (
            <div className="su-deck-share-links-list">
              {shareLinks.map((link) => (
                <div
                  key={link.id}
                  className={`su-deck-share-link-item ${!link.is_active ? 'inactive' : ''}`}
                >
                  <div className="su-deck-share-link-info">
                    <div className="su-deck-share-link-url">
                      {getShareUrl(link.url_slug || link.share_token, deck.name)}
                    </div>
                    <div className="su-deck-share-link-meta">
                      <span className={link.is_active ? 'su-badge-active' : 'su-badge-draft'}>
                        {link.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <span style={{ color: '#666', fontSize: '12px' }}>
                        Created {formatDate(link.created_at)}
                      </span>
                    </div>
                  </div>

                  {/* Settings toggles */}
                  <div className="su-deck-share-link-settings">
                    <button
                      className={`su-deck-toggle-btn ${link.email_notifications ? 'active' : ''}`}
                      onClick={() => handleToggleNotifications(link)}
                      title="Email notifications"
                    >
                      <BellIcon width={14} height={14} />
                      <span>Notify</span>
                    </button>
                    <button
                      className="su-deck-toggle-btn disabled"
                      title="Password protection (Coming soon)"
                      disabled
                    >
                      <LockIcon width={14} height={14} />
                      <span>Password</span>
                    </button>
                    <button
                      className="su-deck-toggle-btn disabled"
                      title="Link expiration (Coming soon)"
                      disabled
                    >
                      <ClockIcon width={14} height={14} />
                      <span>Expires</span>
                    </button>
                  </div>

                  <div className="su-deck-share-link-actions">
                    <button
                      className="su-deck-share-link-btn"
                      onClick={() => handleCopyLink(link.url_slug ?? null, link.share_token, link.id)}
                      title="Copy link"
                      aria-label="Copy link"
                    >
                      {copiedId === link.id ? (
                        <CheckIcon width={16} height={16} style={{ color: '#22c55e' }} />
                      ) : (
                        <CopyIcon width={16} height={16} />
                      )}
                    </button>
                    <button
                      className="su-deck-share-link-btn"
                      onClick={() => handleToggleActive(link)}
                      title={link.is_active ? 'Deactivate' : 'Activate'}
                      aria-label={link.is_active ? 'Deactivate link' : 'Activate link'}
                    >
                      <span style={{ fontSize: '12px' }}>
                        {link.is_active ? 'Disable' : 'Enable'}
                      </span>
                    </button>
                    <button
                      className="su-deck-share-link-btn delete"
                      onClick={() => handleDelete(link.id)}
                      title="Delete link"
                      aria-label="Delete link"
                    >
                      <TrashIcon width={16} height={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="su-deck-modal-footer">
          <button className="su-btn-secondary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default ShareLinkModal;
